import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useAdminStore } from '@/stores/adminStore';

function intentBadge(intent: string) {
  const colors: Record<string, string> = {
    filter: 'bg-accent-blue/15 text-accent-blue',
    stats: 'bg-accent-green/15 text-accent-green',
    count: 'bg-accent-purple/15 text-accent-purple',
    map: 'bg-accent-orange/15 text-accent-orange',
    geospatial: 'bg-accent-orange/15 text-accent-orange',
    distance: 'bg-accent-blue/15 text-accent-blue',
    chart: 'bg-accent-purple/15 text-accent-purple',
    complex: 'bg-accent-red/15 text-accent-red',
    clarify: 'bg-bg-tertiary text-text-muted',
  };
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors[intent] || 'bg-bg-tertiary text-text-muted'}`}>
      {intent}
    </span>
  );
}

export default function QueryFeedTab() {
  const queries = useAdminStore((s) => s.queryFeed);
  const [filterUser, setFilterUser] = useState('');
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const uniqueUsers = [...new Set(queries.map((q) => q.username))];
  const filtered = filterUser
    ? queries.filter((q) => q.username === filterUser)
    : queries;

  if (queries.length === 0) {
    return (
      <div className="text-center text-text-muted text-sm py-12">
        Sin consultas recientes
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Filter */}
      {uniqueUsers.length > 1 && (
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setFilterUser('')}
            className={`text-[10px] px-2.5 py-1 rounded-full border transition-all ${
              !filterUser
                ? 'border-accent-blue text-accent-blue bg-accent-blue/10'
                : 'border-border-subtle text-text-muted hover:border-border-strong'
            }`}
          >
            Todos
          </button>
          {uniqueUsers.map((u) => (
            <button
              key={u}
              onClick={() => setFilterUser(u === filterUser ? '' : u)}
              className={`text-[10px] px-2.5 py-1 rounded-full border transition-all ${
                filterUser === u
                  ? 'border-accent-blue text-accent-blue bg-accent-blue/10'
                  : 'border-border-subtle text-text-muted hover:border-border-strong'
              }`}
            >
              {u}
            </button>
          ))}
        </div>
      )}

      {/* Feed */}
      <div className="space-y-1.5">
        {filtered.map((q, i) => {
          const isExpanded = expandedIdx === i;
          const hasDetails = q.reasoning || q.response;

          return (
            <div
              key={`${q.timestamp}-${i}`}
              className={`px-3 py-2 rounded-lg border ${
                q.success
                  ? 'bg-bg-tertiary/50 border-border-subtle'
                  : 'bg-accent-red/5 border-accent-red/20'
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-semibold text-text-primary">{q.username}</span>
                  {intentBadge(q.intent)}
                </div>
                <span className="text-[10px] text-text-subtle flex-shrink-0 font-mono">
                  {q.response_time_ms > 0 ? `${(q.response_time_ms / 1000).toFixed(1)}s` : ''}
                </span>
              </div>
              <div className="text-[11px] text-text-secondary truncate">{q.query}</div>

              {/* Expand toggle */}
              {hasDetails && (
                <button
                  onClick={() => setExpandedIdx(isExpanded ? null : i)}
                  className="flex items-center gap-1 text-[10px] text-accent-blue hover:text-accent-purple mt-1 transition-colors"
                >
                  {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  {isExpanded ? 'Ocultar' : 'Ver detalle'}
                </button>
              )}

              {/* Expanded details */}
              {isExpanded && (
                <div className="mt-2 space-y-2 border-t border-border-subtle pt-2">
                  {q.reasoning && (
                    <div>
                      <div className="text-[10px] font-semibold text-accent-purple mb-0.5">Razonamiento</div>
                      <div className="text-[11px] text-text-muted leading-relaxed">{q.reasoning}</div>
                    </div>
                  )}
                  {q.response && (
                    <div>
                      <div className="text-[10px] font-semibold text-accent-green mb-0.5">Respuesta</div>
                      <div className="text-[11px] text-text-secondary leading-relaxed">{q.response}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="text-[10px] text-text-subtle mt-1">
                {new Date(q.timestamp).toLocaleTimeString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
